/* Progressive enhancement; compatible with Shopify section reloads. */
(() => {
  const elementName = 'jt-rich-text-disclosure';
  if (customElements.get(elementName)) return;
  let sequence = 0;
  class RichTextDisclosure extends HTMLElement {
    connectedCallback() {
      // A section can repeat block markup for split desktop/mobile layouts.
      // Assign a fresh ID to each actual instance, rather than using block.id.
      this.content = this.querySelector('.jt-rich-text-body__content');
      this.button = this.querySelector('.jt-rich-text-body__toggle');
      this.label = this.querySelector('[data-jt-toggle-label]');
      if (!this.content || !this.button || !this.label) return;
      this.expanded = false;
      this.savedTabIndexes = new Map();
      this.content.id = 'jt-rich-text-content-' + (++sequence);
      this.button.setAttribute('aria-controls', this.content.id);
      this.button.setAttribute('aria-expanded', 'false');
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.button.addEventListener('click', () => {
        this.expanded = !this.expanded;
        this.render();
        if (!this.expanded) {
          // Keep the collapse control visible when a long article shrinks.
          requestAnimationFrame(() => {
            if (!this.isConnected) return;
            const rect = this.button.getBoundingClientRect();
            if (rect.top < 0 || rect.bottom > innerHeight) {
              this.button.scrollIntoView({block: 'center', behavior: 'instant'});
            }
          });
        }
      }, {signal});
      this.schedule = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.measure());
      };
      window.addEventListener('resize', this.schedule, {signal});
      this.content.addEventListener('load', this.schedule, {capture: true, signal});
      this.content.addEventListener('focusin', (event) => {
        // Unclip any focused item, including programmatic focus.
        if (this.hasAttribute('data-collapsed') &&
            event.target.getBoundingClientRect().bottom > this.content.getBoundingClientRect().bottom + 1) {
          this.expanded = true;
          this.render();
        }
      }, {signal});
      if (window.ResizeObserver) {
        this.observer = new ResizeObserver(this.schedule);
        this.observer.observe(this);
        this.observer.observe(this.content);
      }
      if (document.fonts) document.fonts.ready.then(() => {
        if (this.isConnected) this.schedule();
      });
      this.schedule();
    }
    measure() {
      if (!this.isConnected || this.getBoundingClientRect().width === 0) return;
      const firstText = this.content.querySelector('p, li') || this.content;
      const textStyle = getComputedStyle(firstText);
      const lineHeight = parseFloat(textStyle.lineHeight) || parseFloat(textStyle.fontSize) * 1.6;
      const lines = parseInt(getComputedStyle(this).getPropertyValue('--jt-preview-lines'), 10) || 5;
      this.limit = Math.ceil(lineHeight * lines);
      this.style.setProperty('--jt-preview-height', this.limit + 'px');
      this.overflowing = this.content.scrollHeight > this.limit + 2;
      this.render();
    }
    render() {
      const active = this.dataset.mode === 'collapsed' && this.overflowing;
      if (!active) this.expanded = false;
      const collapsed = active && !this.expanded;
      this.toggleAttribute('data-collapsed', collapsed);
      this.setAttribute('data-ready', '');
      this.button.hidden = !active;
      this.button.setAttribute('aria-expanded', String(active && this.expanded));
      this.label.textContent = this.expanded
        ? (this.dataset.collapseLabel || 'Show less')
        : (this.dataset.expandLabel || 'Read more');
      // Prevent keyboard focus reaching clipped links/buttons in the preview.
      this.savedTabIndexes.forEach((value, item) => {
        if (value === null) item.removeAttribute('tabindex');
        else item.setAttribute('tabindex', value);
      });
      this.savedTabIndexes.clear();
      if (collapsed) {
        const boundary = this.content.getBoundingClientRect().bottom;
        this.content.querySelectorAll('a[href],button,input,select,textarea,[tabindex]').forEach(item => {
          const rect = item.getBoundingClientRect();
          if (rect.bottom > boundary + 1) {
            this.savedTabIndexes.set(item, item.getAttribute('tabindex'));
            item.setAttribute('tabindex', '-1');
          }
        });
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.savedTabIndexes?.forEach((value, item) => {
        if (value === null) item.removeAttribute('tabindex');
        else item.setAttribute('tabindex', value);
      });
    }
  }
  customElements.define(elementName, RichTextDisclosure);
})();

